/**
 * 标准配合约束残差模型。
 *
 * 文件职责：
 * 1. 统一线性残差与角度残差的容差来源；
 * 2. 为求解器、约束图与最终校验器提供同一套几何残差判断；
 * 3. 保持输入输出为 cad-core plain data，不依赖 UI、Viewer 或 Object3D。
 */

import * as THREE from 'three';
import {
  AssemblyMateType,
  MateAlignment,
  MateEntityKind,
  type AssemblyMateDefinition,
  type MateEntityRef,
} from '../definition';
import {
  normalizeVector,
  resolveAxisGeometry,
  resolvePointGeometry,
  resolveSphereGeometry,
  type AxisGeometry,
} from './geometry/StandardMateGeometryAccess';

/** 默认线性残差容差。 */
export const DEFAULT_STANDARD_MATE_LINEAR_TOLERANCE = 1e-6;

/** 默认角度残差容差。 */
export const DEFAULT_STANDARD_MATE_ANGULAR_TOLERANCE = 1e-5;

/**
 * 标准配合残差容差。
 */
export interface StandardMateConstraintTolerance {
  /** 长度、距离、径向偏移使用的线性容差。 */
  readonly linear: number;
  /** 方向、法向、轴向夹角使用的角度容差，单位为弧度。 */
  readonly angular: number;
}

/**
 * Concentric Mate 残差。
 */
export interface StandardMateConcentricResidual {
  /** 当前几何是否满足同轴心约束；null 表示几何组合暂无法校验。 */
  readonly satisfied: boolean | null;
  /** 两条中心线、点或点到中心线之间的径向距离。 */
  readonly radialDistance: number | null;
  /** 两条轴线之间按 Mate 语义归一后的角度残差，单位为弧度。 */
  readonly angularDistance: number | null;
  /** 当前 Concentric 可接受的最大径向偏差。 */
  readonly allowedRadialDistance: number | null;
}

/**
 * 解析非负有限容差。
 *
 * @param value 输入值。
 * @param fallback 默认值。
 * @returns 可用于几何判断的容差。
 */
function resolveFiniteTolerance(value: number | undefined, fallback: number): number {
  return typeof value === 'number' && Number.isFinite(value) && value >= 0
    ? value
    : fallback;
}

/**
 * 解析标准配合残差容差。
 *
 * @param input 求解或校验输入中的容差字段。
 * @returns 标准化后的线性/角度容差。
 */
export function resolveStandardMateConstraintTolerance(input: {
  /** 输入线性容差。 */
  readonly epsilon?: number;
  /** 输入角度容差。 */
  readonly angleEpsilon?: number;
}): StandardMateConstraintTolerance {
  return {
    linear: resolveFiniteTolerance(input.epsilon, DEFAULT_STANDARD_MATE_LINEAR_TOLERANCE),
    angular: resolveFiniteTolerance(input.angleEpsilon, DEFAULT_STANDARD_MATE_ANGULAR_TOLERANCE),
  };
}

/**
 * 创建无法校验的 Concentric 残差。
 *
 * @returns 不支持当前几何组合的残差结果。
 */
function createUnsupportedConcentricResidual(): StandardMateConcentricResidual {
  return {
    satisfied: null,
    radialDistance: null,
    angularDistance: null,
    allowedRadialDistance: null,
  };
}

/**
 * 判断实体是否为孔轴类圆形实体。
 *
 * @param entity Mate 实体。
 * @returns 是否为圆柱或圆边。
 */
function isRoundConcentricAxisEntity(entity: MateEntityRef): boolean {
  return entity.entityKind === MateEntityKind.Cylinder
    || entity.entityKind === MateEntityKind.CircularEdge;
}

/**
 * 判断同轴心 Mate 是否应按无向中心线解释轴向。
 *
 * @param mate 同轴心 Mate。
 * @returns 是否忽略轴向正反。
 */
function shouldUseUnsignedConcentricAxisDirection(mate: AssemblyMateDefinition): boolean {
  return isRoundConcentricAxisEntity(mate.firstEntity)
    && isRoundConcentricAxisEntity(mate.secondEntity);
}

/**
 * 计算两个方向的无向平行角度残差。
 *
 * @param first 第一方向。
 * @param second 第二方向。
 * @param tolerance 残差容差。
 * @returns 角度残差；方向退化时返回 null。
 */
export function calculateUnsignedDirectionAngularResidual(
  first: THREE.Vector3,
  second: THREE.Vector3,
  tolerance: StandardMateConstraintTolerance,
): number | null {
  const firstDirection = normalizeVector(first, tolerance.linear);
  const secondDirection = normalizeVector(second, tolerance.linear);
  if (!firstDirection || !secondDirection) {
    return null;
  }
  const angle = firstDirection.angleTo(secondDirection);
  return Math.min(angle, Math.abs(Math.PI - angle));
}

/**
 * 判断两个方向是否在角度容差内平行或反向平行。
 *
 * @param first 第一方向。
 * @param second 第二方向。
 * @param tolerance 残差容差。
 * @returns 是否满足无向平行。
 */
export function isDirectionParallelWithinTolerance(
  first: THREE.Vector3,
  second: THREE.Vector3,
  tolerance: StandardMateConstraintTolerance,
): boolean {
  const residual = calculateUnsignedDirectionAngularResidual(first, second, tolerance);
  return residual !== null && residual <= tolerance.angular;
}

/**
 * 计算 Concentric 轴向残差。
 *
 * @param mate Concentric Mate。
 * @param first 第一轴向。
 * @param second 第二轴向。
 * @param tolerance 残差容差。
 * @returns 角度残差；方向退化时返回 null。
 */
function calculateConcentricDirectionAngularResidual(
  mate: AssemblyMateDefinition,
  first: THREE.Vector3,
  second: THREE.Vector3,
  tolerance: StandardMateConstraintTolerance,
): number | null {
  const firstDirection = normalizeVector(first, tolerance.linear);
  const secondDirection = normalizeVector(second, tolerance.linear);
  if (!firstDirection || !secondDirection) {
    return null;
  }
  const actualAngle = firstDirection.angleTo(secondDirection);
  if (shouldUseUnsignedConcentricAxisDirection(mate)) {
    return Math.min(actualAngle, Math.abs(Math.PI - actualAngle));
  }
  const expectedAngle = mate.alignment === MateAlignment.AntiAligned ? Math.PI : 0;
  return Math.abs(actualAngle - expectedAngle);
}

/**
 * 计算点到轴线的最短距离。
 *
 * @param point 点。
 * @param axis 轴线。
 * @param tolerance 残差容差。
 * @returns 最短距离；轴向退化时返回 null。
 */
function calculatePointAxisDistance(
  point: THREE.Vector3,
  axis: AxisGeometry,
  tolerance: StandardMateConstraintTolerance,
): number | null {
  const axisDirection = normalizeVector(axis.direction, tolerance.linear);
  if (!axisDirection) {
    return null;
  }
  const projection = axis.origin.clone().addScaledVector(
    axisDirection,
    point.clone().sub(axis.origin).dot(axisDirection),
  );
  return point.distanceTo(projection);
}

/**
 * 计算两条轴线的最短距离。
 *
 * @param first 第一轴线。
 * @param second 第二轴线。
 * @param tolerance 残差容差。
 * @returns 最短距离；轴向退化时返回 null。
 */
function calculateAxisAxisDistance(
  first: AxisGeometry,
  second: AxisGeometry,
  tolerance: StandardMateConstraintTolerance,
): number | null {
  const firstDirection = normalizeVector(first.direction, tolerance.linear);
  const secondDirection = normalizeVector(second.direction, tolerance.linear);
  if (!firstDirection || !secondDirection) {
    return null;
  }
  const cross = firstDirection.clone().cross(secondDirection);
  const originDelta = second.origin.clone().sub(first.origin);
  if (cross.lengthSq() <= tolerance.linear * tolerance.linear) {
    return originDelta
      .sub(firstDirection.clone().multiplyScalar(originDelta.dot(firstDirection)))
      .length();
  }
  return Math.abs(originDelta.dot(cross.normalize()));
}

/**
 * 解析同轴心可接受径向偏差。
 *
 * @param maximumRadialDeviation 错位同轴心允许的最大径向偏差。
 * @param tolerance 残差容差。
 * @returns 可接受径向偏差。
 */
function resolveAllowedConcentricRadialDistance(
  maximumRadialDeviation: number | undefined,
  tolerance: StandardMateConstraintTolerance,
): number {
  return typeof maximumRadialDeviation === 'number'
    && Number.isFinite(maximumRadialDeviation)
    && maximumRadialDeviation >= 0
    ? maximumRadialDeviation
    : tolerance.linear;
}

/**
 * 创建 Concentric 残差结果。
 *
 * @param input 残差输入。
 * @returns 标准 Concentric 残差。
 */
function createConcentricResidual(input: {
  /** 径向距离。 */
  readonly radialDistance: number;
  /** 角度距离；点类 Concentric 没有轴向残差。 */
  readonly angularDistance: number | null;
  /** 允许的最大径向偏差。 */
  readonly allowedRadialDistance: number;
  /** 残差容差。 */
  readonly tolerance: StandardMateConstraintTolerance;
}): StandardMateConcentricResidual {
  const angularSatisfied = input.angularDistance === null
    || input.angularDistance <= input.tolerance.angular;
  const radialSatisfied = input.radialDistance <= input.allowedRadialDistance + input.tolerance.linear;
  return {
    satisfied: angularSatisfied && radialSatisfied,
    radialDistance: input.radialDistance,
    angularDistance: input.angularDistance,
    allowedRadialDistance: input.allowedRadialDistance,
  };
}

/**
 * 解析点式 Concentric 实体。
 *
 * @param entity Mate 实体。
 * @returns 可用于中心点约束的点；不适用时返回 null。
 */
function resolveConcentricPointGeometry(entity: MateEntityRef): THREE.Vector3 | null {
  const point = resolvePointGeometry(entity);
  if (point) {
    return point.point;
  }
  const sphere = resolveSphereGeometry(entity);
  return sphere?.center ?? null;
}

/**
 * 评估 Concentric Mate 的统一残差。
 *
 * @param input Concentric 残差输入。
 * @returns Concentric 残差。
 */
export function evaluateStandardMateConcentricResidual(input: {
  /** 当前 Mate。 */
  readonly mate: AssemblyMateDefinition;
  /** 残差容差。 */
  readonly tolerance: StandardMateConstraintTolerance;
  /** 错位同轴心允许的最大径向偏差。 */
  readonly maximumRadialDeviation?: number;
}): StandardMateConcentricResidual {
  if (input.mate.mateType !== AssemblyMateType.Concentric) {
    return createUnsupportedConcentricResidual();
  }

  const firstAxis = resolveAxisGeometry(input.mate.firstEntity);
  const secondAxis = resolveAxisGeometry(input.mate.secondEntity);
  const firstPoint = resolveConcentricPointGeometry(input.mate.firstEntity);
  const secondPoint = resolveConcentricPointGeometry(input.mate.secondEntity);
  const allowedRadialDistance = resolveAllowedConcentricRadialDistance(
    input.maximumRadialDeviation,
    input.tolerance,
  );

  if (firstAxis && secondAxis) {
    const angularDistance = calculateConcentricDirectionAngularResidual(
      input.mate,
      firstAxis.direction,
      secondAxis.direction,
      input.tolerance,
    );
    const radialDistance = calculateAxisAxisDistance(firstAxis, secondAxis, input.tolerance);
    if (angularDistance === null || radialDistance === null) {
      return createUnsupportedConcentricResidual();
    }
    return createConcentricResidual({
      radialDistance,
      angularDistance,
      allowedRadialDistance,
      tolerance: input.tolerance,
    });
  }

  if (firstPoint && secondPoint) {
    return createConcentricResidual({
      radialDistance: firstPoint.distanceTo(secondPoint),
      angularDistance: null,
      allowedRadialDistance,
      tolerance: input.tolerance,
    });
  }

  if (firstPoint && secondAxis) {
    const radialDistance = calculatePointAxisDistance(firstPoint, secondAxis, input.tolerance);
    return radialDistance === null
      ? createUnsupportedConcentricResidual()
      : createConcentricResidual({
          radialDistance,
          angularDistance: null,
          allowedRadialDistance,
          tolerance: input.tolerance,
        });
  }

  if (firstAxis && secondPoint) {
    const radialDistance = calculatePointAxisDistance(secondPoint, firstAxis, input.tolerance);
    return radialDistance === null
      ? createUnsupportedConcentricResidual()
      : createConcentricResidual({
          radialDistance,
          angularDistance: null,
          allowedRadialDistance,
          tolerance: input.tolerance,
        });
  }

  return createUnsupportedConcentricResidual();
}
